import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSync, 
  faFileInvoice, 
  faChartBar, 
  faShieldAlt,
  faCog,
  faCheck,
  faArrowRight,
  faMobile,
  faCloud,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(ScrollTrigger);

const Products = ({ onPurchaseClick }) => {
  const pageRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  const productModules = [
    {
      id: 'sync',
      title: '订单同步模块',
      icon: faSync,
      description: '智能同步多平台订单数据，实现财务业务一体化',
      features: [
        '多平台数据接口对接',
        '实时订单状态同步',
        '智能数据清洗与分类',
        '异常订单自动预警',
        '批量数据处理能力'
      ],
      benefits: [
        '减少90%的手工录入',
        '提升数据准确性至99.8%',
        '实时掌握业务动态'
      ],
      image: '/order-sync.jpg'
    },
    {
      id: 'voucher',
      title: '凭证生成模块',
      icon: faFileInvoice,
      description: '自动生成符合会计准则的记账凭证，提升财务处理效率',
      features: [
        '智能科目匹配',
        '批量凭证生成',
        '多级审核流程',
        '凭证模板管理',
        '错误凭证自动检测'
      ],
      benefits: [
        '凭证生成效率提升300%',
        '减少人工错误率95%',
        '符合财务规范要求'
      ],
      image: '/voucher-generation.jpg'
    },
    {
      id: 'report',
      title: '财务报表模块',
      icon: faChartBar,
      description: '多维度财务分析报表，为企业决策提供数据支撑',
      features: [
        '实时财务报表生成',
        '多维度数据分析',
        '可视化图表展示',
        '自定义报表模板',
        '报表数据导出'
      ],
      benefits: [
        '报表生成时间缩短80%',
        '支持20+种报表类型',
        '数据可视化展示'
      ],
      image: '/financial-report.jpg'
    },
    {
      id: 'security',
      title: '安全保障模块',
      icon: faShieldAlt,
      description: '银行级安全防护，全方位保障数据和资金安全',
      features: [
        '数据加密存储',
        '多重身份验证',
        '权限分级管理',
        '操作日志记录',
        '安全审计功能'
      ],
      benefits: [
        '通过ISO27001认证',
        '99.99%系统可用性',
        '零安全事故记录'
      ],
      image: '/security-module.jpg'
    }
  ];

  const advantages = [
    {
      icon: faCloud,
      title: '云端部署',
      description: 'SaaS模式，无需本地部署，随时随地访问'
    },
    {
      icon: faMobile,
      title: '移动办公',
      description: '支持手机、平板等移动设备，移动办公更便捷'
    },
    {
      icon: faUsers,
      title: '多用户协作',
      description: '支持多用户同时操作，团队协作更高效'
    },
    {
      icon: faCog,
      title: '灵活配置',
      description: '可根据企业需求灵活配置功能模块'
    }
  ];

  useEffect(() => {
    // 页面入场动画
    gsap.fromTo(
      pageRef.current.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out'
      }
    );
  }, []);

  return (
    <div ref={pageRef} className="page-transition">
      {/* 英雄区域 */}
      <section className="py-20 bg-gradient-hero text-white flex items-center min-h-[300px]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl md:text-3xl font-medium text-blue-100">
            专为物流快运行业打造的智能财务管理解决方案
          </p>
        </div>
      </section>

      {/* 产品模块详情 */}
      <section className="py-20 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              核心功能模块
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              四大核心模块，全面覆盖物流企业财务管理需求
            </p>
          </div>

          {/* 模块选项卡 */}
          <div className="flex flex-wrap justify-center mb-12">
            {productModules.map((module, index) => (
              <button
                key={module.id}
                onClick={() => setActiveTab(index)}
                className={`m-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === index
                    ? 'bg-primary-blue text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={module.icon} className="mr-2" />
                {module.title}
              </button>
            ))}
          </div>

          {/* 模块详情 */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* 左侧：功能介绍 */}
              <div className="p-8 lg:p-12">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center mr-4">
                    <FontAwesomeIcon icon={productModules[activeTab].icon} className="text-white text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {productModules[activeTab].title}
                  </h3>
                </div>
                
                <p className="text-lg text-gray-600 mb-8">
                  {productModules[activeTab].description}
                </p>
                
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">核心功能</h4>
                  <ul className="space-y-3">
                    {productModules[activeTab].features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-3" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">核心优势</h4>
                  <div className="space-y-3">
                    {productModules[activeTab].benefits.map((benefit, index) => (
                      <div key={index} className="bg-blue-50 rounded-lg p-3">
                        <span className="text-primary-blue font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* 右侧：产品截图 */}
              <div className="bg-gradient-primary p-8 lg:p-12 flex items-center justify-center">
                <div className="relative">
                  <img
                    src={productModules[activeTab].image}
                    alt={productModules[activeTab].title}
                    className="rounded-2xl shadow-2xl max-w-full h-auto"
                  />
                  <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-primary-cyan rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={productModules[activeTab].icon} className="text-white text-xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 产品优势 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              产品优势
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              先进的技术架构和丰富的行业经验，为您提供卓越的产品体验
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={advantage.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{advantage.title}</h3>
                <p className="text-gray-600">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 技术架构 */}
      <section className="py-20 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              技术架构
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              采用先进的微服务架构，确保系统的稳定性、可扩展性和安全性
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">前端</span>
                </div>
                <h3 className="text-lg font-bold mb-2">用户界面层</h3>
                <p className="text-gray-600 text-sm mb-4">React + Ant Design</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>响应式设计</li>
                  <li>组件化开发</li>
                  <li>企业级UI组件库</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">后端</span>
                </div>
                <h3 className="text-lg font-bold mb-2">业务逻辑层</h3>
                <p className="text-gray-600 text-sm mb-4">Java + Spring Boot</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>微服务架构</li>
                  <li>RESTful API</li>
                  <li>企业级框架</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">数据</span>
                </div>
                <h3 className="text-lg font-bold mb-2">数据存储层</h3>
                <p className="text-gray-600 text-sm mb-4">MySQL + Redis</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>关系型数据库</li>
                  <li>数据备份</li>
                  <li>缓存优化</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA区域 */}
      <section className="py-20 bg-gradient-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            立即体验云帐天下
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            专业的产品团队为您提供一对一的产品演示和技术支持
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onPurchaseClick}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              立即购买
            </button>
          </div>
        </div>
      </section>
      
      {/* Demo Modal */}
        {/* Note: This should be DemoModal, not PurchaseModal */}
    </div>
  );
};

export default Products;